import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography } from "@mhome/ui";
import FixedSelectInput from "../input/fixed-select-input";

const FixedSelectCell = ({
  value,
  onChange,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  meta = {},
  ...cellProps
}) => {
  const [editValue, setEditValue] = useState(value || "");
  const editContainerRef = useRef(null);

  const { enums = [], defaultValue = "" } = meta;

  // Focus the edit container when editing starts
  useEffect(() => {
    if (isEditing && editContainerRef.current) {
      editContainerRef.current.focus();
    }
  }, [isEditing]);

  const handleCellClick = () => {
    if (!isEditing) {
      // Use current value or default
      const currentValue = value || defaultValue;
      setEditValue(currentValue);
      onEdit();
    }
  };

  const handleSelectChange = (newValue) => {
    setEditValue(newValue);
    onChange(newValue);
    onSave();
  };

  // Create valueSpec for FixedSelectInput
  const createValueSpec = () => {
    return {
      value: editValue,
      enums: enums,
      enumLabels: meta.enumLabels,
      readOnly: false,
    };
  };

  const getDisplayValue = () => {
    const currentValue = value || defaultValue;
    if (!currentValue) return "Select...";

    // Find the display label for the current value
    const enumOption = enums.find((enumValue) => enumValue === currentValue);
    if (enumOption && meta.enumLabels) {
      const enumIndex = enums.indexOf(enumOption);
      return meta.enumLabels[enumIndex] || currentValue;
    }

    return currentValue;
  };

  // Convert MUI spacing to pixels (MUI spacing unit = 8px)
  const spacingToPx = (spacing) =>
    typeof spacing === "number" ? spacing * 8 : spacing;

  const cellStyle = useMemo(
    () => ({
      cursor: "pointer",
      padding: "4px",
      border: isEditing ? `2px solid hsl(var(--primary))` : "none",
      borderRadius: isEditing ? spacingToPx(1) : 0, // 8px
      width: "100%",
      height: "40px",
      display: "flex",
      alignItems: "center",
    }),
    [isEditing]
  );

  return (
    <div
      ref={editContainerRef}
      style={cellStyle}
      onClick={handleCellClick}
      onMouseEnter={(e) => {
        if (!isEditing) {
          e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
    >
      {isEditing ? (
        <div
          ref={editContainerRef}
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FixedSelectInput
            valueSpec={createValueSpec()}
            onChange={handleSelectChange}
            onError={() => {}} // Handle errors if needed
            zIndex={1400}
          />
        </div>
      ) : (
        <Typography
          variant="body2"
          style={{
            paddingLeft: spacingToPx(1), // 8px
            paddingRight: spacingToPx(1), // 8px
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {getDisplayValue()}
        </Typography>
      )}
    </div>
  );
};

export default FixedSelectCell;
